// Kabukicho crossing: the original building kinds, roof materials and roof props of the 2x scene.
// A region agent copies this file as hd-fronts-<region>.ts, registers its own kinds, and adds one import line to
// hd-fronts-index.ts. Kind names are global: prefix new ones with the region (e.g. "okuboGrocer").
import { hash2 } from '../../common/src/util';
import { HT, K, NEON, type NeonColor, px, speckle, dither, bands, disc, frame, shadow, dk, lt, mix, alpha, text, textW } from './hd-kit';
import { registerFront, registerRoof, registerRoofProp } from './hd-registry';
import { windowPane, display, door, awning, lantern, vending, plant, bandSign, bladeSign, fitS } from './hd-parts';

// ---------------------------------------------------------------- roof materials
registerRoof('tile', { parapet: false, paint(g, x, y, w, h) {
    px(g, x, y, w, h, K.kawara);
    for (let j = 2; j < h; j += 6) {
      px(g, x, y + j, w, 1, K.kawaraHi); px(g, x, y + j + 4, w, 1, K.kawaraLo);
      for (let i = (j / 6) % 2 ? 0 : 4; i < w; i += 8) px(g, x + i, y + j + 1, 1, 4, K.kawaraLo);
    }
    px(g, x, y + Math.floor(h / 2) - 2, w, 4, K.kawaraLo); px(g, x, y + Math.floor(h / 2) - 3, w, 1, K.kawaraHi);
    frame(g, x, y, w, h, K.ink);
} });
registerRoof('concrete', { paint(g, x, y, w, h, seed) {
  px(g, x, y, w, h, K.concrete);
  for (let j = 0; j < h; j += 40) for (let i = 0; i < w; i += 56) {
    const v = hash2(x + i + seed, y + j);
    if (v < 0.3) px(g, x + i, y + j, Math.min(56, w - i), Math.min(40, h - j), mix(K.concrete, K.concreteLo, 0.5));
    else if (v > 0.85) px(g, x + i, y + j, Math.min(56, w - i), Math.min(40, h - j), mix(K.concrete, K.concreteHi, 0.25));
    px(g, x + i, y + j, Math.min(56, w - i), 1, K.concreteLo); px(g, x + i, y + j, 1, Math.min(40, h - j), K.concreteLo);
    px(g, x + i + 1, y + j + 1, Math.min(55, w - i - 1), 1, mix(K.concrete, K.concreteHi, 0.4));
  }
  speckle(g, x, y, w, h, K.concreteLo, 0.03, 21, 2);
  // ponded water from the last rain, tinted by the sky
  for (let k = 0; k < Math.floor(w * h / 12000); k++) {
    const px0 = x + 20 + Math.floor(hash2(seed, k + 5) * (w - 40)), py0 = y + 16 + Math.floor(hash2(k + 5, seed) * Math.max(1, h - 30));
    disc(g, px0, py0, 10, 4, mix(K.concrete, K.glass, 0.35)); px(g, px0 - 5, py0 - 2, 6, 1, mix(K.concreteHi, K.glassHi, 0.4));
  }
} });
registerRoof('tar', { paint(g, x, y, w, h, seed) {
  px(g, x, y, w, h, K.tar);
  speckle(g, x, y, w, h, K.tarHi, 0.05, 22); speckle(g, x, y, w, h, K.tarLo, 0.06, 23);
  for (let k = 0; k < 3; k++) px(g, x + 10 + Math.floor(hash2(seed, k) * (w - 60)), y + 10 + Math.floor(hash2(k, seed) * Math.max(1, h - 40)), 44, 22, K.tarHi);
} });
registerRoof('ribbed', { paint(g, x, y, w, h) {
  px(g, x, y, w, h, K.metal);
  for (let i = 3; i < w; i += 6) { px(g, x + i, y, 1, h, K.metalHi); px(g, x + i + 1, y, 1, h, K.metalLo); }
} });

// ---------------------------------------------------------------- roof props
registerRoofProp('ac', ({ g, x, y, bx, by, p, L }) => {
  shadow(g, x + 4, y + 5, 26, 20);
  px(g, x, y, 26, 20, K.ink); px(g, x + 1, y + 1, 24, 13, K.metalHi); px(g, x + 1, y + 14, 24, 5, K.metal); px(g, x + 1, y + 18, 24, 1, K.metalLo);
  disc(g, x + 9, y + 7, 5, 5, K.metalLo); disc(g, x + 9, y + 7, 3, 3, K.ink); px(g, x + 4, y + 7, 11, 1, K.metal); px(g, x + 9, y + 2, 1, 11, K.metal);
  for (let i = 17; i < 24; i += 2) px(g, x + i, y + 3, 1, 9, K.metal);
});
registerRoofProp('tank', ({ g, x, y, bx, by, p, L }) => {
  shadow(g, x + 6, y + 30, 30, 8, 0.4);
  for (const lx of [3, 24]) px(g, x + lx, y + 22, 3, 14, K.metalLo);
  px(g, x + 2, y + 30, 24, 2, K.metalLo);
  bands(g, x, y + 6, 30, 22, K.concreteHi, K.concreteLo, 4);
  for (let i = 0; i < 30; i++) { const s = i < 8 ? 0 : i < 20 ? 1 : 2; px(g, x + i, y + 6, 1, 22, [K.metalHi, K.metal, K.metalLo][s]); }
  disc(g, x + 15, y + 6, 15, 5, K.concreteHi); disc(g, x + 15, y + 6, 11, 3, K.concrete); px(g, x + 13, y + 3, 4, 3, K.metalLo);
  for (let j = 10; j < 28; j += 4) px(g, x + 1, y + j, 28, 1, alpha(K.night, 0.25));
  px(g, x + 26, y + 8, 2, 26, K.ink); // ladder
});
registerRoofProp('hut', ({ g, x, y, bx, by, p, L }) => {
  shadow(g, x + 5, y + 6, 40, 36);
  px(g, x, y, 40, 36, K.ink); px(g, x + 1, y + 1, 38, 22, K.concrete); px(g, x + 1, y + 1, 38, 2, K.concreteHi);
  px(g, x + 1, y + 23, 38, 12, K.wall); px(g, x + 1, y + 23, 38, 1, K.ink);
  px(g, x + 14, y + 25, 12, 10, K.ink); px(g, x + 15, y + 26, 10, 9, K.wallLo); px(g, x + 23, y + 30, 1, 2, K.metalHi);
  px(g, x + 18, y + 24, 4, 1, K.warmHi); L(x + 16, y + 23, 8, 3, K.amber, 0.35);
});
registerRoofProp('vent', ({ g, x, y, bx, by, p, L }) => {
  shadow(g, x + 3, y + 4, 12, 10);
  disc(g, x + 6, y + 6, 6, 5, K.ink); disc(g, x + 6, y + 5, 5, 4, K.metalHi); disc(g, x + 6, y + 6, 3, 2, K.metalLo);
});
registerRoofProp('dish', ({ g, x, y, bx, by, p, L }) => {
  shadow(g, x + 5, y + 6, 18, 14);
  disc(g, x + 9, y + 9, 9, 8, K.ink); disc(g, x + 8, y + 8, 8, 7, K.concreteHi); disc(g, x + 10, y + 10, 5, 4, K.concrete);
  px(g, x + 9, y + 9, 8, 1, K.metalLo); px(g, x + 16, y + 6, 3, 3, K.metal);
});
registerRoofProp('skylight', ({ g, x, y, bx, by, p, L }) => {
  shadow(g, x + 4, y + 4, 32, 24);
  px(g, x, y, 32, 24, K.ink); px(g, x + 2, y + 2, 28, 20, K.glass);
  for (let i = 2; i < 30; i += 7) px(g, x + i, y + 2, 1, 20, K.metal);
  px(g, x + 3, y + 3, 10, 2, K.glassHi); dither(g, x + 2, y + 14, 28, 8, alpha(K.amber, 0.25));
  L(x + 2, y + 2, 28, 20, K.amber, 0.12);
});
registerRoofProp('duct', ({ g, x, y, bx, by, p, L }) => {
  const x2 = bx + Math.round((p[3] as number) * HT), y2 = by + Math.round((p[4] as number) * HT);
  const run = (ax: number, ay: number, bx2: number, by2: number) => {
    if (ay === by2) { const x0 = Math.min(ax, bx2), w = Math.abs(bx2 - ax) + 6; shadow(g, x0 + 3, ay + 5, w, 4); px(g, x0, ay, w, 6, K.ink); px(g, x0, ay + 1, w, 2, K.metalHi); px(g, x0, ay + 3, w, 2, K.metal); for (let i = 8; i < w; i += 24) px(g, x0 + i, ay - 1, 3, 8, K.metalLo); }
    else { const y0 = Math.min(ay, by2), h = Math.abs(by2 - ay) + 6; shadow(g, ax + 5, y0 + 3, 4, h); px(g, ax, y0, 6, h, K.ink); px(g, ax + 1, y0, 2, h, K.metalHi); px(g, ax + 3, y0, 2, h, K.metal); for (let j = 8; j < h; j += 24) px(g, ax - 1, y0 + j, 8, 3, K.metalLo); }
  };
  run(x, y, x2, y); if (y2 !== y) run(x2, y, x2, y2);
});

// ---------------------------------------------------------------- fronts
registerFront('pachinko', { wall: dk(K.pink, 0.78) }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  for (let i = x + 6; i < x + w - 6; i += 6) { const on = ((i - x) / 6) % 2 === 0; px(g, i, y + 11, 3, 3, on ? K.warmHi : K.warmLo); px(g, i, gy - 6, 3, 3, on ? K.warmLo : K.warmHi); }
  L(x + 4, y + 10, w - 8, 5, K.amber, 0.35); L(x + 4, gy - 7, w - 8, 5, K.amber, 0.35, true);
  if (sign) bandSign(g, x + w / 2, y + 15, sign.text, sign.color, L, fitS(sign.text, w));
  display(g, x + 12, gy + 6, 60, gf - 12, K.pink, L);
  door(g, x + 96, gy + 4, 64, gf - 6, K.pink, L);
  display(g, x + w - 72, gy + 6, 60, gf - 12, K.pink, L);
});
registerFront('konbini', { wall: K.plaster }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  px(g, x + 4, gy - 9, w - 8, 2, K.cyan); px(g, x + 4, gy - 7, w - 8, 2, K.white); px(g, x + 4, gy - 5, w - 8, 2, K.pink);
  bandSign(g, x + 44, y + 13, 'コンビニ', 'cyan', L, 1);
  display(g, x + 10, gy + 4, 110, gf - 10, lt(K.white, 0), L);
  door(g, x + 128, gy + 3, 40, gf - 5, K.white, L);
  vending(g, x + 180, gy + gf - 40, dk(K.cyan, 0.45), L); vending(g, x + 204, gy + gf - 40, K.white, L);
  px(g, x + 230, gy + gf - 18, 10, 14, K.ink); px(g, x + 231, gy + gf - 17, 8, 12, dk(K.cyan, 0.55)); px(g, x + 242, gy + gf - 18, 10, 14, K.ink); px(g, x + 243, gy + gf - 17, 8, 12, dk(K.amber, 0.55)); // bins
});
registerFront('izakaya', { wall: K.wood }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  // a short kawara eave over the shopfront
  px(g, x, gy - 12, w, 8, K.kawara); for (let i = 0; i < w; i += 6) px(g, x + i, gy - 12, 1, 8, K.kawaraLo); px(g, x, gy - 12, w, 1, K.kawaraHi); px(g, x, gy - 4, w, 3, alpha(K.night, 0.5));
  if (sign) { const sw = textW(sign.text, 2) + 12; px(g, x + 14, y + 13, sw, 20, K.ink); px(g, x + 15, y + 14, sw - 2, 18, K.woodHi); text(g, sign.text, x + 20, y + 17, K.ink, 2); L(x + 14, y + 13, sw, 20, K.amber, 0.25); }
  for (const wx of [x + 12, x + 170]) { px(g, wx - 2, gy + 6, 64, gf - 14, K.ink); bands(g, wx, gy + 8, 60, gf - 18, K.warmHi, K.warm, 3); for (let i = 0; i < 60; i += 10) px(g, wx + i, gy + 8, 1, gf - 18, K.woodLo); for (let j = 8; j < gf - 10; j += 8) px(g, wx, gy + j, 60, 1, K.woodLo); L(wx, gy + 8, 60, gf - 18, K.amber, 0.35, true); }
  door(g, x + 100, gy + 4, 56, gf - 6, K.amber, L);
  for (let i = 0; i < 3; i++) { px(g, x + 100 + i * 19, gy + 4, 18, 18, K.redLo); px(g, x + 100 + i * 19, gy + 4, 18, 2, K.redDk); } // noren
  disc(g, x + 128, gy + 13, 4, 4, K.white);
  lantern(g, x + 86, gy + 6, K.red, L); lantern(g, x + 162, gy + 6, K.red, L);
  plant(g, x + 80, gy + gf - 20);
});
registerFront('tower', { wall: K.wall, upper: 'curtain' }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  if (sign) bladeSign(g, x + 6, y + 14, sign.text, sign.color, L, 3);
  px(g, x + 20, gy - 6, w - 30, 5, K.ink); px(g, x + 20, gy - 6, w - 30, 2, K.concreteHi); px(g, x + 20, gy - 1, w - 30, 3, alpha(K.night, 0.45)); // canopy
  door(g, x + w / 2 - 22, gy + 4, 44, gf - 6, K.amber, L);
  display(g, x + 26, gy + 8, 22, gf - 14, K.cyan, L, false); display(g, x + w - 48, gy + 8, 22, gf - 14, K.cyan, L, false);
});
registerFront('hotel', { wall: dk(K.pink, 0.72), wallAc: true }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  if (sign) bladeSign(g, x + 6, y + 8, sign.text, sign.color, L, 3);
  px(g, x + 70, gy - 4, 90, 6, K.ink); px(g, x + 71, gy - 3, 88, 3, K.pink); px(g, x + 71, gy, 88, 2, alpha(K.night, 0.5)); L(x + 70, gy - 4, 90, 6, K.pink, 0.45, true);
  door(g, x + 96, gy + 6, 40, gf - 8, K.pink, L);
  display(g, x + 160, gy + 8, 70, gf - 16, K.amber, L, false);
  plant(g, x + 76, gy + gf - 20); plant(g, x + 142, gy + gf - 20);
});
registerFront('apartment', { wall: K.plasterLo, wallAc: true }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  for (let i = 0; i < 4; i++) {
    const dx = x + 14 + i * 60;
    px(g, dx - 1, gy + 5, 24, gf - 7, K.ink); px(g, dx, gy + 6, 22, gf - 8, K.metal); px(g, dx + 18, gy + 20, 2, 4, K.metalHi);
    px(g, dx + 6, gy, 10, 3, K.warmHi); L(dx + 4, gy - 1, 14, 5, K.amber, 0.35, true);
    px(g, dx + 30, gy + 10, 14, 10, K.ink); px(g, dx + 31, gy + 11, 12, 8, K.metalLo); // mailbox / meter
  }
});
registerFront('bar', { wall: K.wallLo }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  const n = Math.max(1, Math.floor(w / 96));
  for (let i = 0; i < n; i++) {
    const sx = x + i * (w / n), col: NeonColor = i % 2 ? 'cyan' : 'pink';
    if (i === 0 && sign) bandSign(g, sx + 48, y + 13, sign.text, sign.color, L, fitS(sign.text, w));
    else bandSign(g, sx + 48, y + 15, i % 2 ? 'BAR' : 'バー', col, L, 1);
    door(g, sx + 20, gy + 6, 26, gf - 8, NEON[col], L); frame(g, sx + 17, gy + 3, 32, gf - 3, NEON[col]);
    windowPane(g, sx + 58, gy + 10, 24, 14, 0.3, L); px(g, sx + 58, gy + 10, 24, 14, alpha(NEON[col], 0.35));
    plant(g, sx + 4, gy + gf - 20);
  }
});
registerFront('ramen', { wall: K.wood }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  if (sign) bandSign(g, x + w / 2, y + 12 + Math.max(0, upper - 34), sign.text, sign.color, L, fitS(sign.text, w));
  awning(g, x + 8, gy - 2, w - 16, 10, K.red, K.redLo);
  display(g, x + 12, gy + 16, 70, gf - 22, K.amber, L, false);
  dither(g, x + 14, gy + 16, 66, 8, alpha(K.white, 0.35)); // steam
  px(g, x + 12, gy + gf - 14, 70, 4, K.woodHi); // counter
  door(g, x + 104, gy + 14, 52, gf - 16, K.amber, L);
  for (let i = 0; i < 3; i++) px(g, x + 104 + i * 18, gy + 14, 17, 14, K.white), px(g, x + 104 + i * 18, gy + 26, 17, 2, K.redLo);
  lantern(g, x + 90, gy + 14, K.red, L); lantern(g, x + 160, gy + 14, K.red, L);
  // menu board on the wall
  px(g, x + 168, gy + 14, 14, 20, K.ink); px(g, x + 169, gy + 15, 12, 18, K.woodLo); for (let j = 18; j < 32; j += 3) px(g, x + 171, gy + j, 8, 1, K.warmHi);
});
registerFront('kabuki', { wall: dk(K.pink, 0.68), upper: 'none' }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  // the gate: a deep lit arch with a lantern row and the kabuki mask above
  const ax = x + w / 2 - 50, aw = 100, ay = y + 30, ah = h - 34;
  px(g, ax - 6, ay - 8, aw + 12, ah + 8, K.ink);
  px(g, ax - 4, ay - 6, aw + 8, 6, K.amber); px(g, ax - 4, ay - 6, aw + 8, 2, K.warmHi); L(ax - 4, ay - 6, aw + 8, 6, K.amber, 0.5);
  bands(g, ax, ay, aw, ah, dk(K.pink, 0.6), K.pink, 5);
  for (let j = 0; j < 10; j++) px(g, ax, ay + j, aw, 1, alpha(K.night, 0.6 - j * 0.06)); // the soffit shadow
  px(g, ax, ay, 4, ah, alpha(K.night, 0.5)); px(g, ax + aw - 3, ay, 3, ah, alpha(K.night, 0.3));
  for (let i = 0; i < 6; i++) lantern(g, ax + 8 + i * 16, ay + 10, i % 2 ? K.amber : K.red, L);
  px(g, ax + 6, ay + ah - 6, aw - 12, 6, lt(K.pink, 0.35)); // lit floor inside
  L(ax, ay + 20, aw, ah - 20, K.pink, 0.6, true);
  // mask emblem
  const mx = x + w / 2 - 14, my = y + 4;
  px(g, mx - 3, my - 1, 34, 27, K.amber); px(g, mx - 2, my, 32, 25, K.ink);
  disc(g, mx + 14, my + 12, 12, 11, K.white); px(g, mx + 4, my + 8, 8, 2, K.pink); px(g, mx + 16, my + 8, 8, 2, K.pink);
  px(g, mx + 6, my + 10, 3, 2, K.ink); px(g, mx + 19, my + 10, 3, 2, K.ink); px(g, mx + 12, my + 17, 4, 2, K.pink); px(g, mx + 3, my + 14, 2, 6, K.pink); px(g, mx + 23, my + 14, 2, 6, K.pink);
  L(mx - 3, my - 1, 34, 27, K.white, 0.35);
  if (sign) bladeSign(g, x + w - 40, y + 20, sign.text, sign.color, L, 3);
  bladeSign(g, x + 8, y + 20, 'ホテル', 'pink', L, 3);
  vending(g, x + 36, gy + gf - 40, dk(K.pink, 0.4), L);
});
registerFront('arcade', { wall: K.wallLo }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  if (sign) bladeSign(g, x + 6, y + 12, sign.text, sign.color, L, 3);
  const gx = x + 36, gw = w - 44, gh = h - 26;
  px(g, gx - 2, y + 14, gw + 4, gh + 2, K.ink); px(g, gx, y + 16, gw, gh - 2, dk(K.cyan, 0.85));
  for (let j = y + 20; j + 12 < y + 14 + gh; j += 18) for (let i = gx + 4; i + 12 <= gx + gw - 2; i += 16) {
    const v = hash2(i, j), col = v < 0.4 ? K.cyan : v < 0.75 ? K.pink : K.amber;
    px(g, i, j, 12, 12, K.ink); px(g, i + 1, j + 1, 10, 7, col); px(g, i + 2, j + 2, 3, 2, lt(col, 0.6)); px(g, i + 1, j + 9, 10, 2, K.metalLo);
    L(i, j, 12, 8, col, 0.22, true);
  }
  for (let k = 0; k < gh; k++) px(g, gx + 20 + Math.floor(k / 2), y + 16 + k, 2, 1, alpha(K.white, 0.15));
});
registerFront('shutter', { wall: K.wall }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  for (const sx of [x + 12, x + 120]) {
    px(g, sx - 2, gy + 2, 96, gf - 3, K.ink);
    for (let j = 0; j < gf - 8; j += 3) { px(g, sx, gy + 4 + j, 92, 2, K.metal); px(g, sx, gy + 6 + j, 92, 1, K.metalLo); }
    px(g, sx + 10, gy + 14, 18, 6, alpha(K.pink, 0.4)); px(g, sx + 40, gy + 22, 24, 4, alpha(K.cyan, 0.35)); // tags
  }
  px(g, x + 120, gy + gf - 8, 92, 3, K.warmHi); L(x + 120, gy + gf - 9, 92, 4, K.amber, 0.3, true); // light under a half-open shutter
});
registerFront('office', { wall: K.plasterLo }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  door(g, x + w / 2 - 18, gy + 6, 36, gf - 8, K.cyan, L);
  display(g, x + 20, gy + 10, 50, gf - 18, dk(K.cyan, 0.3), L, false);
  if (w > 160) display(g, x + w - 70, gy + 10, 50, gf - 18, dk(K.cyan, 0.3), L, false);
});
registerFront('karaoke', { wall: dk(K.cyan, 0.8) }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  if (sign) bandSign(g, x + w / 2, y + 12, sign.text, sign.color, L, fitS(sign.text, w));
  door(g, x + 30, gy + 6, 40, gf - 8, K.cyan, L); frame(g, x + 27, gy + 3, 46, gf - 3, K.cyan);
  for (let i = 0; i < 3; i++) { windowPane(g, x + 100 + i * 50, gy + 12, 34, 16, 0.5, L); frame(g, x + 99 + i * 50, gy + 11, 36, 18, K.pink); }
});
